<template>
    <div>
        <div class="flex border-b border-grayxlight mb-4 text-[11px]">
            <div class=" text-darkblue mr-4 cursor-pointer leading-4" v-for="({ index }, tabIndex) in sortedShiftTabs" :key="index"
                :class="{ 'semibold text-[13px] border-b border-x-darkblue': (index === currentShiftIndex) }"
                @click="switchTab(index)">Смена {{ tabIndex + 1 }}</div>
            <div
                class="text-[11px] text-gray"
                :class="isAddShiftBlockedByDayStateDocument ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
                @click="addTab"
            >
                + Добавить смену
            </div>
        </div>
        <div v-for="(shift, index) in store.currentShifts" :key="index">
            <ShiftsItem ref="shiftItemRef" :shift :schedule :day-types="dayTypes" v-if="index == currentShiftIndex && shift" @delete="onShiftDelete" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Schedule } from '@/types'
import ShiftsItem from './ShiftsItem.vue'
import dayjs from 'dayjs'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from 'vue-toast-notification'
import { parseDurationToDecimalHours } from '@/utils/duration'

const store = useScheduleStore()
const authStore = useAuthStore()

const toast = useToast({ position: 'top' })

interface Props {
    date: string,
    schedule: Schedule
}

const { schedule, date } = defineProps<Props>()

const emit = defineEmits(['switch'])

const currentShiftIndex = ref<number>(0)
const pendingShiftIndex = ref<number | null>(null)
const shiftItemRef = ref()
// Use in schedule only assignable DayTypes
const dayTypes = computed(() => store.dayTypes.filter(item => item.assignable))
const effectiveCurrentShifts = computed(() => {
    return store.currentShifts.map((shift, index) => {
        if (index !== currentShiftIndex.value) return shift
        if (!store.currentShift) return shift

        return store.currentShift
    })
})

const sortedShiftTabs = computed(() => {
    return effectiveCurrentShifts.value
        .map((shift, index) => {
            const priority = shift.day_state_document ? 0 : shift.is_overtime ? 2 : 1
            const planStart = shift.plan_work_start_at
            const start = planStart ? dayjs.utc(planStart) : null

            return { index, priority, start: start?.isValid() ? start.valueOf() : Infinity }
        })
        .sort((left, right) => (left.priority - right.priority) || (left.start - right.start) || (left.index - right.index))
})

const hasDayStateDocumentInCurrentDay = computed(() => {
    return effectiveCurrentShifts.value.some((shift) => Boolean(shift?.day_state_document))
})
const isAddShiftBlockedByDayStateDocument = computed(() => {
    return effectiveCurrentShifts.value.some((shift) => {
        if (!shift?.day_state_document) return false

        return parseDurationToDecimalHours(shift.duration) > 0
    })
})

const addTab = () => {
    if (isAddShiftBlockedByDayStateDocument.value) return

    if(authStore.permissions?.can_create_schedule) {
        let overtime = false
    const fallbackStart = dayjs.utc(`${date}T09:00:00`)
    const fallbackEnd = dayjs.utc(`${date}T18:00:00`)
    if (!fallbackStart.isValid() || !fallbackEnd.isValid()) {
        toast.error('Некорректная дата смены')
        return
    }
    let start = fallbackStart.toISOString()
    let end = fallbackEnd.toISOString()
    // set start time of new shift is after 1 hour of last shift 
    if (store.currentShifts.length) {
        overtime = true
        const validPlanEnds = store.currentShifts
            .map((item) => item.plan_work_end_at)
            .filter((value): value is string => Boolean(value && dayjs.utc(value).isValid()))

        if (validPlanEnds.length) {
            const maxEnd = validPlanEnds.reduce((latest, current) => (
                dayjs.utc(current).isAfter(dayjs.utc(latest)) ? current : latest
            ))
            const nextStart = dayjs.utc(maxEnd)
            const nextEnd = dayjs.utc(nextStart).add(1, 'hour')
            if (!nextStart.isValid() || !nextEnd.isValid()) {
                toast.error('Некорректное время смены')
                return
            }
            start = nextStart.toISOString()
            end = nextEnd.toISOString()
        }
    } else {
        const defaultStart = dayjs.utc(`${date}T${store.currentDepartment?.opening_time || '09:00:00'}`)
        const defaultEnd = dayjs.utc(`${date}T${store.currentDepartment?.closing_time || '18:00:00'}`)

        if (!defaultStart.isValid() || !defaultEnd.isValid()) {
            toast.error('Некорректное время смены')
            return
        }

        start = defaultStart.toISOString()
        end = defaultEnd.toISOString()
    }
    if (hasDayStateDocumentInCurrentDay.value) {
        overtime = true
    }
    const shift = {
        department: schedule.department.id,
        employment: schedule.id,
        day_type: dayTypes.value[0].id,
        position: schedule.position.id,
        plan_work_start_at: start,
        plan_work_end_at: end,
        fact_work_start_at: null,
        fact_work_end_at: null,
        is_overtime: overtime,
        date: date
    }
    store.addCurrentShift(shift)
    switchTab(store.currentShifts.length - 1)
    store.handleSaveButtonAvailability()
    } else {
        toast.error('У вас нет прав на данное действие')
    }
}

const switchTab = (index: number) => {
    if (index === currentShiftIndex.value) return

    if(store.isCurrentShiftChanged || 
    (store.currentShift && !store.currentShift?.id)
) {
        pendingShiftIndex.value = index
        emit('switch')
    } else {
        setCurrentShiftIndex(index)
    }
}

const setCurrentShiftIndex = (index: number) => {
    currentShiftIndex.value = index
    store.setCurrentShift(store.currentShifts[index])
}

const completePendingSwitch = () => {
    if (pendingShiftIndex.value === null) return

    setCurrentShiftIndex(pendingShiftIndex.value)
    pendingShiftIndex.value = null
}

const cancelPendingSwitch = () => {
    pendingShiftIndex.value = null
}

const validateCurrentShiftPlanTime = () => {
    const shiftItem = Array.isArray(shiftItemRef.value) ? shiftItemRef.value[0] : shiftItemRef.value

    return shiftItem?.validatePlanTime() ?? true
}

const deleteShiftFromCurrentShifts = () => {
    store.currentShifts.splice(currentShiftIndex.value, 1)
    if(currentShiftIndex.value === 0) switchTab(0)
    else switchTab(currentShiftIndex.value - 1)
}

const onShiftDelete = async () => {
    if(store.currentShifts[currentShiftIndex.value].id) {
        const res = await store.shiftDelete(store.currentShifts[currentShiftIndex.value])
        if(res === 'ok') {
            deleteShiftFromCurrentShifts()
            await store.fetchStats()
        }
    } else {
        deleteShiftFromCurrentShifts()
    }
    
}

defineExpose({
    completePendingSwitch,
    cancelPendingSwitch,
    validateCurrentShiftPlanTime
})

</script>
